import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import useBrains from '@/hooks/useBrains';
import { selectAccountSlug, selectCampaignId } from '@/redux/session/selectors';
import { WhatsappTemplate } from '@/shared/types/broadcast';
import { Campaign, NBACampaignGoal } from '@/shared/types/campaigns';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelDisplayName } from '@/util/channelName';
import { createYupResolver } from '@/util/forms/createYupResolver';

import { useCampaignBuildLogic } from '../../components/BuildSteps/useCampaignsBuildForm';
import { useCampaignBuildFormInit } from '../../hooks/useCampaignBuildFormInit';
import { useCampaigns } from '../../hooks/useCampaigns';
import { useCampaignSenders } from '../../hooks/useCampaignSenders';
import { useCampaignSubscribers } from '../../hooks/useCampaignSubscribers';
import {
  DraftCampaignSender,
  updateDraftCampaign,
  updateDraftPaymentRecoverySender,
  setDraftPaymentRecoverySender,
  setDraftPaymentRecoverySenders,
  setDraftSelectedChannels,
  setDraftPaymentRecoveryGoal,
  setDraftPaymentRecoveryGuidelines,
} from '../../redux/actions';
import {
  selectDraftCampaignName,
  selectDraftAiAgentData,
  selectDraftCampaignType,
  selectDraftPaymentRecoverySender,
  selectDraftPaymentRecoverySenders,
  selectDraftPaymentRecoveryGoal,
  selectDraftPaymentRecoveryGuidelines,
  selectDraftOutreachGoal,
  selectDraftSelectedChannels,
  selectDraftVoiceSettings,
} from '../../redux/selectors';
import {
  areAllSendersComplete,
  buildSequentialSenders as buildSequentialSendersUtil,
  CampaignForm,
  formatTemplateComponents,
  isFirstMessageComplete as isFirstMessageCompleteUtil,
  reconstructAutonomousChannels,
  replaceCampaignSendersSequentially,
} from '../../utils';
import { getFormUnresolvedVariables } from '../../utils/utils';
import { campaignBuildSchema } from '../../validator';

/**
 * Agent goal options for the campaign
 */
export const AGENT_GOAL_OPTIONS = [
  {
    value: 'promise_to_pay',
    labelKey: 'campaigns.builddebtcampaign.goals.promise_to_pay.label',
    descriptionKey: 'campaigns.builddebtcampaign.goals.promise_to_pay.description',
  },
  {
    value: 'claim_of_payment',
    labelKey: 'campaigns.builddebtcampaign.goals.claim_of_payment.label',
    descriptionKey: 'campaigns.builddebtcampaign.goals.claim_of_payment.description',
  },
  {
    value: 'secure_confirm_payment',
    labelKey: 'campaigns.builddebtcampaign.goals.secure_confirm_payment.label',
    descriptionKey: 'campaigns.builddebtcampaign.goals.secure_confirm_payment.description',
    disabled: true,
  },
] as const;

export type AgentGoal = (typeof AGENT_GOAL_OPTIONS)[number]['value'];

/**
 * Maps UI goal values to API goal values
 */
export const GOAL_TO_NBA_GOAL: Record<AgentGoal, NBACampaignGoal> = {
  promise_to_pay: 'ptp',
  claim_of_payment: 'payment_claim',
  secure_confirm_payment: 'payment_api',
};

/**
 * Maps API goal values back to UI goal values
 */
export const NBA_GOAL_TO_GOAL: Record<NBACampaignGoal, AgentGoal> = {
  ptp: 'promise_to_pay',
  payment_claim: 'claim_of_payment',
  payment_api: 'secure_confirm_payment',
};

export interface ValidationErrors {
  aiAgent?: string;
  goal?: string;
  channel?: string;
  firstMessage?: string;
}

/**
 * Creates a base sender configuration object with default values
 */
const createBaseSender = () => ({
  integrationId: '',
  integrationType: '' as IntegrationType,
  content: '',
  subject: '',
  template: undefined,
  headerParameters: [],
  bodyParameters: [],
  footerParameters: [],
  headerMediaParam: undefined,
});

/**
 * Custom hook that handles all business logic for the Build1 page
 * @returns Object containing state, handlers, and derived data
 */
export const useBuildDebtCampaignLogic = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [_formKey, setFormKey] = useState(0);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const campaignId = useSelector(selectCampaignId);
  const slug = useSelector(selectAccountSlug);
  const draftCampaignName = useSelector(selectDraftCampaignName);
  const draftAiAgentData = useSelector(selectDraftAiAgentData);
  const draftPaymentRecoverySender = useSelector(selectDraftPaymentRecoverySender);
  const draftPaymentRecoverySenders = useSelector(selectDraftPaymentRecoverySenders);
  const draftSelectedChannels = useSelector(selectDraftSelectedChannels);
  const draftVoiceSettings = useSelector(selectDraftVoiceSettings);
  const selectedChannels = useMemo(() => draftSelectedChannels || [''], [draftSelectedChannels]);
  const draftCampaignType = useSelector(selectDraftCampaignType);
  const selectedGoal = (useSelector(selectDraftPaymentRecoveryGoal) as AgentGoal) || '';
  const outreachGoal = useSelector(selectDraftOutreachGoal) || '';
  const guidelines = useSelector(selectDraftPaymentRecoveryGuidelines) || '';

  const { campaign, getStatus, updateCampaignAsync } = useCampaigns(campaignId);

  const isPaymentRecovery = (draftCampaignType || campaign?.type) === 'payment_recovery';
  // For validation: outreach stores goal in a different Redux field
  const hasGoal = isPaymentRecovery ? !!selectedGoal : !!outreachGoal;

  // NBA campaigns always use sequential (multi-channel) senders
  const hasSequentialSenderMessages = campaign?.nba_config != null;
  const {
    campaign_senders,
    createCampaignSenderAsync,
    updateCampaignSenderAsync,
    deleteCampaignSenderAsync,
  } = useCampaignSenders(campaignId);
  const { listStatus: brainsListStatus } = useBrains();
  const { campaign_subscribers } = useCampaignSubscribers(campaignId);

  // Initialize form in parent component (needed for useCampaignBuildLogic)
  const methods = useForm<CampaignForm>({
    resolver: createYupResolver<CampaignForm>(campaignBuildSchema),
    defaultValues: {
      primarySender: createBaseSender(),
      fallbackSenders: [
        createBaseSender(),
        {
          ...createBaseSender(),
          waitTimeMinutes: 7 * 24 * 60, // Default to 7 days
        },
      ],
      fallbackIfNotDelivered: false,
      fallbackIfNoResponse: false,
    },
  });

  /**
   * Use hook to initialize form from react-query data (not Redux)
   */
  const { listStatus: sendersListStatus } = useCampaignBuildFormInit(
    campaignId,
    methods,
    setFormKey
  );

  // Track if we've already initialized from API data
  const initializedChannelsRef = useRef<string | undefined>(undefined);
  const initializedNbaConfigRef = useRef<string | undefined>(undefined);

  /**
   * Sync selectedChannels state and Redux draftSenders when data loads
   * Priority: Redux (for returning from Message page) > API data (for initial load)
   */
  useEffect(() => {
    // Wait for BOTH the senders list and the campaign (nba_config carries the
    // additional-channel mappings) so the one-shot init doesn't lock in a
    // voice-only channel list before integration_mappings has loaded (MOV-17461).
    const shouldInitialize =
      sendersListStatus === 'success' &&
      getStatus === 'success' &&
      campaignId &&
      initializedChannelsRef.current !== campaignId;

    if (!shouldInitialize) {
      return;
    }

    // If Redux already has draftSelectedChannels, use it as authoritative source
    if (draftSelectedChannels && draftSelectedChannels.length > 0) {
      // Sync primary sender form state from first sender if available
      const primary = draftPaymentRecoverySenders?.[0];
      if (primary?.integrationId) {
        methods.setValue('primarySender', {
          integrationId: primary.integrationId,
          integrationType: (primary.integrationType as IntegrationType) || ('' as IntegrationType),
          content: primary.content || '',
          subject: primary.subject || '',
          template: primary.template,
          headerParameters: primary.headerParameters || [],
          bodyParameters: primary.bodyParameters || [],
          footerParameters: primary.footerParameters || [],
          headerMediaParam: primary.headerMediaParam,
        });
      } else if (draftPaymentRecoverySender?.integrationId) {
        // Fallback to singular sender when sequential senders are not available
        methods.setValue('primarySender', {
          integrationId: draftPaymentRecoverySender.integrationId,
          integrationType:
            (draftPaymentRecoverySender.integrationType as IntegrationType) ||
            ('' as IntegrationType),
          content: draftPaymentRecoverySender.content || '',
          subject: draftPaymentRecoverySender.subject || '',
          template: draftPaymentRecoverySender.template,
          headerParameters: draftPaymentRecoverySender.headerParameters || [],
          bodyParameters: draftPaymentRecoverySender.bodyParameters || [],
          footerParameters: draftPaymentRecoverySender.footerParameters || [],
          headerMediaParam: draftPaymentRecoverySender.headerMediaParam,
        });
      }
      initializedChannelsRef.current = campaignId;
      return;
    }

    // Check if Redux already has sequential senders data (returning from Messages page)
    if (draftPaymentRecoverySenders && draftPaymentRecoverySenders.length > 0) {
      const channels = draftPaymentRecoverySenders
        .filter((s) => s.integrationId)
        .map((s) => s.integrationId)
        .filter((id, idx, arr) => arr.indexOf(id) === idx); // deduplicate (WA follow-up reuses primary integration)
      if (channels.length > 0) {
        dispatch(setDraftSelectedChannels(channels));
      }
      // Sync primary sender form state from first sender
      const primary = draftPaymentRecoverySenders[0];
      if (primary?.integrationId) {
        methods.setValue('primarySender', {
          integrationId: primary.integrationId,
          integrationType: (primary.integrationType as IntegrationType) || ('' as IntegrationType),
          content: primary.content || '',
          subject: primary.subject || '',
          template: primary.template,
          headerParameters: primary.headerParameters || [],
          bodyParameters: primary.bodyParameters || [],
          footerParameters: primary.footerParameters || [],
          headerMediaParam: primary.headerMediaParam,
        });
      }
      initializedChannelsRef.current = campaignId;
      return;
    }

    // Check if Redux already has data (returning from Message page)
    if (draftPaymentRecoverySender?.integrationId) {
      dispatch(setDraftSelectedChannels([draftPaymentRecoverySender.integrationId]));
      // Also sync form state from Redux
      methods.setValue('primarySender', {
        integrationId: draftPaymentRecoverySender.integrationId,
        integrationType:
          (draftPaymentRecoverySender.integrationType as IntegrationType) ||
          ('' as IntegrationType),
        content: draftPaymentRecoverySender.content || '',
        subject: draftPaymentRecoverySender.subject || '',
        template: draftPaymentRecoverySender.template,
        headerParameters: draftPaymentRecoverySender.headerParameters || [],
        bodyParameters: draftPaymentRecoverySender.bodyParameters || [],
        footerParameters: draftPaymentRecoverySender.footerParameters || [],
        headerMediaParam: draftPaymentRecoverySender.headerMediaParam,
      });
      initializedChannelsRef.current = campaignId;
      return;
    }

    // Otherwise, initialize from API data
    if (campaign_senders && campaign_senders.length > 0) {
      const apiPrimarySender = campaign_senders[0];
      if (apiPrimarySender?.integration_id) {
        // Autonomous campaigns keep additional channels in nba_config.integration_mappings
        // (campaign_senders only holds the primary). Reconstruct the full list so a saved
        // WhatsApp/second channel reappears on reopen (MOV-17461).
        dispatch(
          setDraftSelectedChannels(
            reconstructAutonomousChannels(
              apiPrimarySender.integration_id,
              campaign?.nba_config?.integration_mappings
            )
          )
        );

        // Initialize Redux with the API data for sharing with Message page
        dispatch(
          setDraftPaymentRecoverySender({
            integrationId: apiPrimarySender.integration_id,
            integrationType: apiPrimarySender.integration_type as string,
            content:
              (apiPrimarySender.template as { body?: string; text?: string })?.body ||
              (apiPrimarySender.template as { body?: string; text?: string })?.text ||
              '',
            subject: (apiPrimarySender.template as { subject?: string })?.subject || '',
            template: apiPrimarySender.template as unknown as WhatsappTemplate | undefined,
          })
        );
      }
    }

    initializedChannelsRef.current = campaignId;
  }, [
    campaign_senders,
    campaign?.nba_config?.integration_mappings,
    getStatus,
    sendersListStatus,
    campaignId,
    dispatch,
    draftSelectedChannels,
    draftPaymentRecoverySender,
    draftPaymentRecoverySenders,
    methods,
  ]);

  /**
   * Initialize goal and guidelines from campaign's nba_config when data loads
   * Only initializes if Redux doesn't already have values (to preserve user changes)
   */
  useEffect(() => {
    const nbaConfig = campaign?.nba_config;
    if (
      getStatus !== 'success' ||
      !nbaConfig ||
      !campaignId ||
      initializedNbaConfigRef.current === campaignId
    ) {
      return;
    }

    // Initialize goal from API if Redux doesn't have a value
    if (nbaConfig.goal && !selectedGoal) {
      const uiGoal = NBA_GOAL_TO_GOAL[nbaConfig.goal];
      if (uiGoal) {
        dispatch(setDraftPaymentRecoveryGoal(uiGoal));
      }
    }

    // Initialize guidelines from API if Redux doesn't have a value
    if (nbaConfig.guidelines && !guidelines) {
      dispatch(setDraftPaymentRecoveryGuidelines(nbaConfig.guidelines));
    }

    initializedNbaConfigRef.current = campaignId;
  }, [campaign, getStatus, campaignId, dispatch, selectedGoal, guidelines]);

  /**
   * Use campaign build logic to get available integrations/channels and templates
   */
  const { allIntegrations, loadingIntegrations, onPrimarySenderChange } =
    useCampaignBuildLogic(methods);

  // Build sequential senders in Redux when data is ready and they haven't been built yet
  useEffect(() => {
    if (
      hasSequentialSenderMessages &&
      allIntegrations.length > 0 &&
      selectedChannels[0] &&
      (!draftPaymentRecoverySenders || draftPaymentRecoverySenders.length === 0)
    ) {
      // Use draftPaymentRecoverySender (singular) as seed for the primary —
      // it's populated from API data before the form is initialized.
      const senders = buildSequentialSendersUtil(
        selectedChannels,
        allIntegrations,
        draftPaymentRecoverySender?.integrationId
          ? [draftPaymentRecoverySender as DraftCampaignSender]
          : undefined
      );

      // Seed follow-up senders from campaign's channel_follow_up_defaults if available
      const followUpDefaults = (
        campaign?.nba_config as {
          channel_follow_up_defaults?: Record<
            string,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            { template?: any; subject?: string }
          >;
        }
      )?.channel_follow_up_defaults;

      if (followUpDefaults) {
        for (let i = 1; i < senders.length; i++) {
          const sender = senders[i];
          const channelDefaults = followUpDefaults[sender.integrationType];
          if (!channelDefaults) {
            continue;
          }

          if (sender.integrationType === IntegrationType.WHATSAPP && channelDefaults.template) {
            const template = channelDefaults.template as WhatsappTemplate;
            // Extract parameters from template components (same as useCampaignBuildFormInit)
            const headerParameters: Array<{ name: string }> = [];
            const bodyParameters: Array<{ name: string }> = [];
            const footerParameters: Array<{ name: string }> = [];

            if (template.components && Array.isArray(template.components)) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              template.components.forEach((component: any) => {
                if (component.parameters && Array.isArray(component.parameters)) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  component.parameters.forEach((param: any) => {
                    if (param.text !== undefined) {
                      switch (component.type?.toUpperCase()) {
                        case 'HEADER':
                          headerParameters.push({ name: param.text || '' });
                          break;
                        case 'BODY':
                          bodyParameters.push({ name: param.text || '' });
                          break;
                        case 'FOOTER':
                          footerParameters.push({ name: param.text || '' });
                          break;
                      }
                    }
                  });
                }
              });
            }

            senders[i] = {
              ...sender,
              template,
              headerParameters,
              bodyParameters,
              footerParameters,
            };
          } else if (sender.integrationType === IntegrationType.EMAIL && channelDefaults.subject) {
            senders[i] = { ...sender, subject: channelDefaults.subject };
          }
        }
      }

      dispatch(setDraftPaymentRecoverySenders(senders));
    }
  }, [
    hasSequentialSenderMessages,
    allIntegrations,
    selectedChannels,
    draftPaymentRecoverySenders,
    draftPaymentRecoverySender,
    campaign?.nba_config,
    dispatch,
  ]);

  // Get the first selected integration for display in the First Message card
  const firstIntegration = allIntegrations.find(
    (integration) => integration.integration_id === selectedChannels[0]
  );

  /**
   * Check if the first message is complete based on integration type
   * Uses the utility function from utils.ts
   */
  const isFirstMessageComplete = useCallback(
    (): boolean => isFirstMessageCompleteUtil(draftPaymentRecoverySender, selectedChannels[0]),
    [draftPaymentRecoverySender, selectedChannels]
  );

  /**
   * Sync sequential senders to Redux from the given channels array
   */
  const syncSequentialSenders = useCallback(
    (channels: string[]) => {
      dispatch(
        setDraftPaymentRecoverySenders(
          buildSequentialSendersUtil(channels, allIntegrations, draftPaymentRecoverySenders)
        )
      );
    },
    [allIntegrations, draftPaymentRecoverySenders, dispatch]
  );

  /**
   * Handle channel selection change.
   * Sequential senders: rebuilds the full senders array in Redux.
   * Single-channel: updates only the primary sender in Redux.
   */
  const handleChannelChange = useCallback(
    (index: number, value: string) => {
      const newChannels = [...selectedChannels];
      newChannels[index] = value;
      dispatch(setDraftSelectedChannels(newChannels));

      // Update form state for primary channel
      if (index === 0 && value) {
        onPrimarySenderChange(value);
      }

      if (hasSequentialSenderMessages) {
        syncSequentialSenders(newChannels);
      } else {
        // Single-channel: update primary sender Redux state
        if (index === 0 && value) {
          const integration = allIntegrations.find((i) => i.integration_id === value);
          dispatch(
            updateDraftPaymentRecoverySender({
              integrationId: value,
              integrationType: integration?.type || '',
              content: '',
              subject: '',
              template: undefined,
              headerParameters: [],
              bodyParameters: [],
              footerParameters: [],
              headerMediaParam: undefined,
            })
          );
        }
      }
    },
    [
      selectedChannels,
      onPrimarySenderChange,
      allIntegrations,
      dispatch,
      hasSequentialSenderMessages,
      syncSequentialSenders,
    ]
  );

  /**
   * Add another channel selector
   */
  const handleAddChannel = useCallback(() => {
    dispatch(setDraftSelectedChannels([...selectedChannels, '']));
  }, [selectedChannels, dispatch]);

  /**
   * Remove a channel selector (only for additional channels, index > 0)
   */
  const handleRemoveChannel = useCallback(
    (index: number) => {
      if (index <= 0) {
        return;
      }

      const newChannels = selectedChannels.filter((_, i) => i !== index);
      dispatch(setDraftSelectedChannels(newChannels));

      if (hasSequentialSenderMessages) {
        syncSequentialSenders(newChannels);
      }
    },
    [selectedChannels, hasSequentialSenderMessages, syncSequentialSenders, dispatch]
  );

  /**
   * Clear a specific validation error
   */
  const clearValidationError = useCallback((field: keyof ValidationErrors) => {
    setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  /**
   * Handle goal selection change
   */
  const handleGoalChange = useCallback(
    (value: AgentGoal) => {
      dispatch(setDraftPaymentRecoveryGoal(value));
      if (validationErrors.goal) {
        clearValidationError('goal');
      }
    },
    [dispatch, validationErrors.goal, clearValidationError]
  );

  /**
   * Handle guidelines change (from BlockNote editor)
   */
  const handleGuidelinesChange = useCallback(
    (markdown: string) => {
      dispatch(setDraftPaymentRecoveryGuidelines(markdown));
    },
    [dispatch]
  );

  /**
   * Check if all sequential senders are complete
   */
  const areAllMessagesComplete = useCallback(
    (): boolean => areAllSendersComplete(draftPaymentRecoverySenders),
    [draftPaymentRecoverySenders]
  );

  /**
   * Compute the subtitle text for the Messages card.
   * Sequential senders: lists all selected channel types (e.g. "WhatsApp · Email")
   * Single-channel: shows channel type + integration name (e.g. "Email · My Mailgun")
   */
  const messagesCardSubtitle = (() => {
    if (hasSequentialSenderMessages) {
      const filledChannels = selectedChannels.filter(Boolean);
      if (filledChannels.length === 0) {
        return t('campaigns.messages.card_empty_subtitle');
      }
      if (!areAllMessagesComplete()) {
        return t('campaigns.messages.card_incomplete');
      }
      // List unique channel display names
      const channelNames = filledChannels
        .map((id) => {
          const integration = allIntegrations.find((i) => i.integration_id === id);
          return integration?.type
            ? getChannelDisplayName(integration.type as IntegrationType)
            : null;
        })
        .filter(Boolean);
      return [...new Set(channelNames)].join(' · ');
    }

    // Single-channel
    if (!firstIntegration) {
      return t('campaigns.builddebtcampaign.first_message_empty_subtitle');
    }
    if (!isFirstMessageComplete()) {
      return t('campaigns.builddebtcampaign.first_message_incomplete');
    }
    return `${getChannelDisplayName(firstIntegration.type as IntegrationType)} · ${firstIntegration.name}`;
  })();

  /**
   * Validate the build form including custom fields
   */
  const validateBuildForm = useCallback(async (): Promise<boolean> => {
    const errors: ValidationErrors = {};

    // Validate AI Agent
    if (!draftAiAgentData?.brain_parent_id) {
      errors.aiAgent = t('validation.required');
    }

    // Validate Goal (outreach uses draftOutreachGoal, payment recovery uses selectedGoal)
    if (!hasGoal) {
      errors.goal = t('validation.required');
    }

    // Validate Channel
    if (!selectedChannels[0]) {
      errors.channel = t('validation.required');
    }

    // Validate messages
    if (hasSequentialSenderMessages) {
      if (!areAllMessagesComplete()) {
        errors.firstMessage = t('validation.required');
      }
    } else {
      if (!isFirstMessageComplete()) {
        errors.firstMessage = t('validation.required');
      }
    }

    const unresolved = getFormUnresolvedVariables(methods.getValues(), campaign_subscribers);
    if (unresolved.length > 0) {
      errors.firstMessage = t('campaigns.missing_variables', { 0: unresolved.join(', ') });
    }

    setValidationErrors(errors);

    // Check if there are any custom validation errors
    const hasCustomErrors = Object.keys(errors).length > 0;

    // Also run the form validation for primarySender fields
    const isFormValid = await methods.trigger();

    if (hasCustomErrors || !isFormValid) {
      const hasOtherErrors = Object.keys(errors).some((k) => k !== 'firstMessage') || !isFormValid;
      if (unresolved.length > 0 && !hasOtherErrors) {
        toast.error(t('campaigns.missing_variables', { 0: unresolved.join(', ') }));
      } else {
        toast.error(t('campaigns.build.fix_errors_before_continue'));
      }
    }

    return !hasCustomErrors && isFormValid;
  }, [
    methods,
    draftAiAgentData,
    hasGoal,
    selectedChannels,
    t,
    hasSequentialSenderMessages,
    areAllMessagesComplete,
    isFirstMessageComplete,
    campaign_subscribers,
  ]);

  /**
   * Shared function to save campaign data (used by both Continue and Save & Exit)
   * @param validateAll - Whether to validate all required fields (true for Continue, false for Save & Exit)
   * @returns The saved campaign object, or null if validation failed
   */
  const saveSenderData = useCallback(
    async (validateAll: boolean = true): Promise<boolean> => {
      // Validate form only if validateAll is true (for Continue)
      if (validateAll) {
        const isValid = await validateBuildForm();
        if (!isValid) {
          return false;
        }
      }

      try {
        // The messages page edits the opener settings on its own form, so the
        // Redux copy is the authored value here. Only this campaign's copy: the
        // draft survives a campaign switch and these keys reach sender metadata.
        const authoredVoiceSettings =
          draftVoiceSettings?.campaignId === campaignId ? draftVoiceSettings.settings : undefined;
        const formData = {
          ...methods.getValues(),
          ...(authoredVoiceSettings && { voiceSettings: authoredVoiceSettings }),
        };

        // Save senders only if we have a primary sender with integration
        const hasPrimarySender = formData.primarySender?.integrationId?.trim();
        const hasExistingSenders = campaign_senders && campaign_senders.length > 0;

        if (hasPrimarySender) {
          // Check if we have existing senders to delete and replace them
          await replaceCampaignSendersSequentially(
            campaignId,
            formData,
            campaign_senders || [],
            createCampaignSenderAsync,
            updateCampaignSenderAsync,
            (campaignId: string, senderId: string) =>
              deleteCampaignSenderAsync({ campaign_id: campaignId, sender_id: senderId })
          );
        } else if (hasExistingSenders) {
          // No new sender data, but we have existing senders to delete
          for (const existingSender of campaign_senders) {
            await deleteCampaignSenderAsync({
              campaign_id: campaignId,
              sender_id: existingSender.sender_id,
            });
          }
        }
        // If no primary sender and no existing senders, skip sender operations entirely

        // Build integration_mappings from all selected channels
        const integration_mappings: Record<string, string> = {};
        for (const channelId of selectedChannels) {
          if (!channelId) {
            continue;
          }
          const integration = allIntegrations.find((i) => i.integration_id === channelId);
          if (integration?.type) {
            integration_mappings[integration.type] = channelId;
          }
        }

        // Build channel_follow_up_defaults from sequential senders (when flag is on)
        // Iterate over follow-up senders (index 1+) by integration type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let channel_follow_up_defaults: Record<string, any> | undefined;
        if (hasSequentialSenderMessages && draftPaymentRecoverySenders) {
          channel_follow_up_defaults = {};

          for (let i = 1; i < draftPaymentRecoverySenders.length; i++) {
            const followUp = draftPaymentRecoverySenders[i];
            if (!followUp) {
              continue;
            }

            if (followUp.integrationType === IntegrationType.WHATSAPP && followUp.template) {
              const formattedTemplate = formatTemplateComponents(
                followUp.template,
                followUp.headerParameters || [],
                followUp.bodyParameters || [],
                followUp.footerParameters || [],
                followUp.headerMediaParam
              );
              if (formattedTemplate) {
                channel_follow_up_defaults.whatsapp = { template: formattedTemplate };
              }
            } else if (
              followUp.integrationType === IntegrationType.EMAIL &&
              followUp.subject?.trim()
            ) {
              channel_follow_up_defaults.email = { subject: followUp.subject.trim() };
            }
          }

          // Clear if empty
          if (Object.keys(channel_follow_up_defaults).length === 0) {
            channel_follow_up_defaults = undefined;
          }
        }

        const needsEmailFallback =
          integration_mappings[IntegrationType.EMAIL] &&
          !channel_follow_up_defaults?.email?.subject;

        if (needsEmailFallback) {
          const primarySender = hasSequentialSenderMessages
            ? draftPaymentRecoverySenders?.[0]
            : draftPaymentRecoverySender;

          if (
            primarySender?.integrationType === IntegrationType.EMAIL &&
            primarySender.subject?.trim()
          ) {
            channel_follow_up_defaults = {
              ...channel_follow_up_defaults,
              email: { subject: primarySender.subject.trim() },
            };
          }
        }

        // Update campaign with nba_config and brain data
        // Payment recovery goals are mapped via GOAL_TO_NBA_GOAL; outreach goals are free-text
        const nbaGoal = isPaymentRecovery
          ? selectedGoal
            ? GOAL_TO_NBA_GOAL[selectedGoal]
            : undefined
          : outreachGoal || undefined;
        const brainParentId = draftAiAgentData?.brain_parent_id;
        const brainVersion = draftAiAgentData?.brain_version;
        await updateCampaignAsync({
          campaign_id: campaignId,
          is_agentic: true,
          type: isPaymentRecovery
            ? 'payment_recovery'
            : (draftCampaignType as Campaign['type']) || 'marketing',
          // Only include brain fields if both have actual values
          ...(brainParentId &&
            brainVersion != null && {
              brain_parent_id: brainParentId,
              brain_version: brainVersion,
            }),
          nba_config: {
            ...campaign?.nba_config,
            // Only include integration_mappings if it has at least one key
            // to avoid backend validation error on empty object
            ...(Object.keys(integration_mappings).length > 0 && { integration_mappings }),
            goal: nbaGoal,
            guidelines: guidelines || undefined,
            ...(channel_follow_up_defaults && { channel_follow_up_defaults }),
          },
        });

        return true;
      } catch (error) {
        console.error('Error saving campaign:', error);
        return false;
      }
    },
    [
      allIntegrations,
      campaign?.nba_config,
      campaignId,
      campaign_senders,
      createCampaignSenderAsync,
      deleteCampaignSenderAsync,
      draftAiAgentData?.brain_parent_id,
      draftAiAgentData?.brain_version,
      draftCampaignType,
      draftPaymentRecoverySender,
      draftPaymentRecoverySenders,
      draftVoiceSettings,
      guidelines,
      hasSequentialSenderMessages,
      isPaymentRecovery,
      methods,
      outreachGoal,
      selectedChannels,
      selectedGoal,
      updateCampaignAsync,
      updateCampaignSenderAsync,
      validateBuildForm,
    ]
  );

  const handleSaveAndExit = useCallback(async () => {
    const isValid = await saveSenderData(false); // Don't validate form for Save & Exit
    if (!isValid) {
      return; // Validation failed or save error
    }

    navigate(`/${slug}/campaigns`);
  }, [saveSenderData, navigate, slug]);

  const handleContinue = useCallback(async () => {
    const isValid = await saveSenderData(true); // Validate all fields for Continue
    if (!isValid) {
      return; // Validation failed or save error
    }

    // Navigate to review step with the actual campaign ID (important for draft campaigns)
    navigate(`/${slug}/campaigns/${campaignId}/payment-recovery/review`);
  }, [saveSenderData, navigate, slug, campaignId]);

  const handlePrevious = useCallback(() => {
    navigate(`/${slug}/campaigns/${campaignId}/payment-recovery/setup`);
  }, [navigate, slug, campaignId]);

  /**
   * Handle AI agent selection and update Redux directly
   */
  const handleBrainSubmit = useCallback(
    (selection: { brain_parent_id: string; brain_version: number; brain_name: string } | null) => {
      if (!selection) {
        return;
      }
      dispatch(
        updateDraftCampaign({
          brain_parent_id: selection.brain_parent_id,
          brain_version: selection.brain_version,
          brain_name: selection.brain_name,
        })
      );
      if (validationErrors.aiAgent) {
        clearValidationError('aiAgent');
      }
    },
    [dispatch, validationErrors.aiAgent, clearValidationError]
  );

  /**
   * Navigate to the message configuration page.
   * Sequential senders are built reactively by the effect above.
   */
  const navigateToInitialMessage = useCallback(() => {
    if (hasSequentialSenderMessages) {
      navigate(`/${slug}/campaigns/${campaignId}/payment-recovery/messages`);
    } else {
      navigate(`/${slug}/campaigns/${campaignId}/payment-recovery/message`);
    }
  }, [hasSequentialSenderMessages, navigate, slug, campaignId]);

  // Check if we're still loading essential data
  const isLoadingEssentialData =
    getStatus === 'pending' || brainsListStatus === 'pending' || sendersListStatus === 'pending';

  // Prefer Redux value (preserves unsaved edits when navigating between steps),
  // fall back to campaign API data for initial load.
  let guidelinesInitialMarkdown = '';
  if (guidelines) {
    guidelinesInitialMarkdown = guidelines;
  } else if (campaign?.nba_config) {
    guidelinesInitialMarkdown = campaign.nba_config.guidelines ?? '';
  }

  if (!guidelinesInitialMarkdown) {
    guidelinesInitialMarkdown = t('campaigns.builddebtcampaign.guidelines.default_text');
  }

  return {
    // State
    selectedGoal,
    selectedChannels,
    guidelines,
    guidelinesInitialMarkdown,
    validationErrors,
    draftCampaignName,
    draftAiAgentData,
    isLoadingEssentialData,

    // Derived data
    allIntegrations,
    loadingIntegrations,
    firstIntegration,
    hasSequentialSenderMessages,
    messagesCardSubtitle,

    // Handlers
    handleGoalChange,
    handleGuidelinesChange,
    handleChannelChange,
    handleAddChannel,
    handleRemoveChannel,
    handleBrainSubmit,
    handlePrevious,
    handleContinue,
    handleSaveAndExit,
    saveSenderData,
    navigateToInitialMessage,
    clearValidationError,
  };
};
